import { getImportTypeLabel } from '../../app/terminology/imports';
import { ActionList, Badge, BlockStack, Button, Icon, InlineStack, Popover, ProgressBar, Text } from '@shopify/polaris';
import { ArchiveIcon, ArrowDownIcon, FileIcon, MenuHorizontalIcon, UndoIcon, ViewIcon } from '@shopify/polaris-icons';
import React, { useId, useState } from 'react';
import type { FitmentImportHistoryItem } from '../../app/api/agent';

const formatStageLabel = (stage?: string) => {
    if (!stage) {
        return null;
    }

    switch (stage.trim().toLowerCase()) {
        case 'processing':
            return 'Processing';
        case 'resolving_skus':
            return 'Resolving SKUs';
        case 'waiting_shopify_sync':
            return 'Waiting for Shopify sync';
        case 'processing_recovered_skus':
            return 'Processing recovered SKUs';
        case 'parsing':
            return 'Parsing';
        case 'applying':
            return 'Applying';
        case 'pricing':
            return 'Updating prices';
        case 'reading':
            return 'Reading tags';
        case 'reading_collection_mappings':
            return 'Reading collection mappings';
        case 'resolving_collections':
            return 'Resolving collections';
        case 'creating_vehicles':
            return 'Creating vehicle records';
        case 'reading_configurator_data':
            return 'Reading configurator data';
        case 'writing_configurator_data':
            return 'Writing variant attributes';
        case 'validating_configurator_products':
            return 'Validating products';
        case 'assigning_configurator_products':
            return 'Assigning configurator';
        case 'exporting':
            return 'Exporting';
        case 'tagging':
            return 'Tagging';
        case 'completed':
            return 'Completed';
        case 'completed_with_skipped_skus':
            return 'Completed with skipped SKUs';
        case 'completed_with_review':
            return 'Completed with review';
        case 'failed':
            return 'Failed';
        default:
            return stage;
    }
};

export const getStatusBadge = (state: string) => {
    const normalizedState = state.trim().toLowerCase();

    if (normalizedState === 'completed' || normalizedState === 'complete') {
        return <Badge tone="success">Complete</Badge>;
    }

    if (normalizedState === 'completed with skipped skus') {
        return <Badge tone="warning">Completed with skips</Badge>;
    }

    if (normalizedState === 'completed with review') {
        return <Badge tone="warning">Completed with review</Badge>;
    }

    if (normalizedState === 'pending review') {
        return <Badge tone="warning">Pending review</Badge>;
    }

    if (normalizedState === 'applied') {
        return <Badge tone="success">Applied</Badge>;
    }

    if (normalizedState === 'undone') {
        return <Badge>Undone</Badge>;
    }

    if (normalizedState === 'failed' || normalizedState === 'error') {
        return <Badge tone="critical">Error</Badge>;
    }

    if (normalizedState === 'queued' || normalizedState === 'pending') {
        return <Badge tone="info">Queued</Badge>;
    }

    if (normalizedState === 'waiting for shopify sync') {
        return <Badge progress="partiallyComplete" tone="info">Waiting for Shopify sync</Badge>;
    }

    if (normalizedState === 'resolving skus') {
        return <Badge progress="partiallyComplete" tone="info">Resolving SKUs</Badge>;
    }

    if (normalizedState === 'processing recovered skus') {
        return <Badge progress="partiallyComplete" tone="info">Processing recovered SKUs</Badge>;
    }

    if (normalizedState === 'processing' || normalizedState === 'in_progress' || normalizedState === 'in progress') {
        return <Badge progress="partiallyComplete" tone="info">In progress</Badge>;
    }

    if (normalizedState === 'exporting') {
        return <Badge progress="partiallyComplete" tone="info">Exporting</Badge>;
    }

    if (normalizedState === 'tagging in progress') {
        return <Badge progress="partiallyComplete" tone="info">Tagging in progress</Badge>;
    }

    if (normalizedState === 'tagging complete') {
        return <Badge tone="success">Tagging complete</Badge>;
    }

    if (normalizedState === 'tagging failed') {
        return <Badge tone="critical">Tagging failed</Badge>;
    }

    if (normalizedState === 'cancelled' || normalizedState === 'canceled') {
        return <Badge tone="warning">Cancelled</Badge>;
    }

    return <Badge>{state}</Badge>;
};

export const getMappingRulesSummary = (item: FitmentImportHistoryItem) => {
    if (item.importFormat === 'atd_price_file') {
        return 'Updates variant prices from MAP and inventory item costs';
    }

    if (item.importFormat === 'product_tags') {
        return 'Stages Shopify product tags for review before changing fitment mappings';
    }

    if (item.importFormat === 'fitment_export') {
        return 'Exports active product and variant fitment assignments';
    }

    if (item.importFormat === 'csv_xlsx') {
        return item.csvXlsxImportMode === 'replace_matched_skus'
            ? 'Replaced existing fitments for matched SKUs'
            : 'Appended fitments to existing mappings';
    }

    if (item.importFormat === 'collection_mappings_csv_xlsx') {
        return 'Upserted vehicle-to-collection mappings; product mappings were unchanged';
    }

    if (item.importFormat === 'configurator_data_csv_xlsx') {
        return item.configuratorAssignmentMode === 'update_only'
            ? 'Updated Shopify variant metafields; product assignments were unchanged'
            : 'Updated Shopify variant metafields and validated bulk configurator assignments';
    }

    if (!item.restrictToVendor) {
        return 'Store-wide vendor mapping';
    }

    const vendorLabel = item.selectedVendorName || 'Unknown vendor';
    const normalizeLabel = item.normalizeSku ? 'SKU normalization on' : 'SKU normalization off';

    if (!item.useVendorSkuSuffixRule) {
        return `${vendorLabel} · SKU suffix rule off · ${normalizeLabel}`;
    }

    const suffixRule = item.vendorSkuSuffixRule?.trim() || '(empty)';
    const updateLabel = item.updateSavedVendorSkuSuffixRule ? 'saved as default' : 'import only';

    return `${vendorLabel} · Suffix "${suffixRule}" (${updateLabel}) · ${normalizeLabel}`;
};

const formatCount = (value?: number) => (typeof value === 'number' ? value.toLocaleString() : '0');

type ImportMetric = {
    label: string;
    value: string;
};

const hasRecordMetrics = (item: FitmentImportHistoryItem) => [
    item.parsedRecordCount, item.appliedRecordCount, item.declaredRecordCount,
    item.csvXlsxImportedVehicleRecordCount, item.csvXlsxAppliedAssignmentCount,
    item.collectionMappingCreatedVehicleRecordCount, item.collectionMappingAddedCount,
    item.collectionMappingReassignedCount, item.collectionMappingUnchangedCount,
    item.collectionMappingSkippedRowCount, item.configuratorMatchedVariantCount,
    item.configuratorUpdatedMetafieldCount, item.configuratorUpdatedVariantCount,
    item.configuratorAssignedProductCount, item.configuratorSkippedProductCount, item.configuratorFailedWriteCount,
].some(value => typeof value === 'number');

const getRecordMetrics = (item: FitmentImportHistoryItem): ImportMetric[] => {
    if (item.importFormat === 'fitment_export') {
        return [{
            label: 'Assignments exported',
            value: formatCount(item.appliedRecordCount ?? item.parsedRecordCount),
        }];
    }

    if (item.importFormat === 'csv_xlsx') {
        const metrics: ImportMetric[] = [
            { label: 'Rows parsed', value: formatCount(item.parsedRecordCount) },
        ];

        if (typeof item.csvXlsxImportedVehicleRecordCount === 'number') {
            metrics.push({
                label: 'Vehicle records',
                value: formatCount(item.csvXlsxImportedVehicleRecordCount),
            });
        }

        metrics.push({
            label: 'Product assignments',
            value: formatCount(item.csvXlsxAppliedAssignmentCount ?? item.appliedRecordCount),
        });

        return metrics;
    }

    if (item.importFormat === 'collection_mappings_csv_xlsx') {
        return [
            { label: 'Rows parsed', value: formatCount(item.parsedRecordCount) },
            { label: 'Vehicles created', value: formatCount(item.collectionMappingCreatedVehicleRecordCount) },
            { label: 'Mappings added', value: formatCount(item.collectionMappingAddedCount) },
            { label: 'Reassigned', value: formatCount(item.collectionMappingReassignedCount) },
            { label: 'Already mapped', value: formatCount(item.collectionMappingUnchangedCount) },
            { label: 'Review rows', value: formatCount(item.collectionMappingSkippedRowCount) },
        ];
    }

    if (item.importFormat === 'configurator_data_csv_xlsx') {
        return [
            { label: 'Rows parsed', value: formatCount(item.parsedRecordCount) },
            { label: 'Variants matched', value: formatCount(item.configuratorMatchedVariantCount) },
            { label: 'Metafields updated', value: formatCount(item.configuratorUpdatedMetafieldCount) },
            { label: 'Products assigned', value: formatCount(item.configuratorAssignedProductCount) },
            { label: 'Products skipped', value: formatCount(item.configuratorSkippedProductCount) },
            { label: 'Failed writes', value: formatCount(item.configuratorFailedWriteCount) },
        ];
    }

    return [
        { label: 'Rows declared', value: formatCount(item.declaredRecordCount) },
        { label: 'Rows parsed', value: formatCount(item.parsedRecordCount) },
        { label: 'Rows applied', value: formatCount(item.appliedRecordCount) },
    ];
};

const formatImportDate = (value: string) => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }).format(date);
};

type ImportDateGroup = {
    key: string;
    label: string;
};

const getLocalDateKey = (date: Date) => [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
].join('-');

const getImportDateGroup = (value: string): ImportDateGroup => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return {
            key: `unknown-${value}`,
            label: 'Date unavailable',
        };
    }

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const key = getLocalDateKey(date);

    if (key === getLocalDateKey(today)) {
        return { key, label: 'Today' };
    }

    if (key === getLocalDateKey(yesterday)) {
        return { key, label: 'Yesterday' };
    }

    return {
        key,
        label: new Intl.DateTimeFormat(undefined, {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        }).format(date),
    };
};

const getHistoryItemTone = (state: string) => {
    const normalizedState = state.trim().toLowerCase();

    if (normalizedState.includes('failed') || normalizedState.includes('error')) {
        return 'critical';
    }

    if (normalizedState.includes('skipped') || normalizedState.includes('pending review') || normalizedState.includes('with review')) {
        return 'warning';
    }

    if (
        normalizedState.includes('processing')
        || normalizedState.includes('queued')
        || normalizedState.includes('waiting')
        || normalizedState.includes('resolving')
        || normalizedState.includes('exporting')
        || normalizedState.includes('tagging in progress')
    ) {
        return 'info';
    }

    if (
        normalizedState.includes('complete')
        || normalizedState === 'applied'
        || normalizedState === 'tagging complete'
    ) {
        return 'success';
    }

    return 'default';
};

type StageProgress = {
    label: string;
    value: number;
};

const getStageProgress = (item: FitmentImportHistoryItem): StageProgress | null => {
    const stage = item.processingStage?.trim().toLowerCase();
    const declared = item.declaredRecordCount;
    const parsed = item.parsedRecordCount;
    const applied = item.appliedRecordCount;

    if (stage === 'parsing' && typeof declared === 'number' && declared > 0 && typeof parsed === 'number') {
        const value = Math.round((Math.min(parsed, declared) / declared) * 100);
        return {
            label: `Parsing rows ${formatCount(parsed)} / ${formatCount(declared)}`,
            value,
        };
    }

    if (stage === 'applying' && typeof parsed === 'number' && parsed > 0 && typeof applied === 'number') {
        const value = Math.round((Math.min(applied, parsed) / parsed) * 100);
        return {
            label: `Applying rows ${formatCount(applied)} / ${formatCount(parsed)}`,
            value,
        };
    }

    if (stage === 'pricing' && typeof parsed === 'number' && parsed > 0 && typeof applied === 'number') {
        const value = Math.round((Math.min(applied, parsed) / parsed) * 100);
        return {
            label: `Updating matched variants ${formatCount(applied)} / ${formatCount(parsed)}`,
            value,
        };
    }

    if (stage === 'exporting') {
        return {
            label: 'Writing fitment export CSV',
            value: typeof item.progressPercent === 'number'
                ? Math.max(0, Math.min(100, item.progressPercent))
                : 0,
        };
    }

    if (stage === 'tagging') {
        if (typeof item.taggingTotalCount === 'number' && item.taggingTotalCount > 0) {
            const processed = Math.max(0, Math.min(item.taggingProcessedCount ?? 0, item.taggingTotalCount));
            const value = Math.round((processed / item.taggingTotalCount) * 100);
            const actionLabel = item.taggingPhase?.trim().toLowerCase() === 'fetching'
                ? 'Fetching product tags'
                : 'Applying product tags';

            return {
                label: `${actionLabel} ${formatCount(processed)} / ${formatCount(item.taggingTotalCount)}`,
                value,
            };
        }

        return {
            label: 'Tagging changed products',
            value: typeof item.progressPercent === 'number'
                ? Math.max(0, Math.min(100, item.progressPercent))
                : 0,
        };
    }

    return null;
};

const getStageDiagnostics = (item: FitmentImportHistoryItem) => {
    switch (item.processingStage?.trim().toLowerCase()) {
        case 'parsing':
            return 'Parsing the input file and normalizing source rows.';
        case 'applying':
            return 'Writing parsed rows to fitment records and deduplicating.';
        case 'resolving_skus':
            return 'Looking up missing SKUs directly in Shopify.';
        case 'waiting_shopify_sync':
            return 'Waiting for Shopify product and variant sync to finish.';
        case 'processing_recovered_skus':
            return 'Applying fitment rows for SKUs recovered from Shopify.';
        case 'pricing':
            return 'Matching ATD part numbers to Shopify variant SKUs and updating prices.';
        case 'exporting':
            return 'Writing assigned product fitments to a downloadable CSV.';
        case 'reading':
            return 'Reading synced Shopify product tags.';
        case 'reading_collection_mappings':
            return 'Reading and normalizing the collection mapping spreadsheet.';
        case 'resolving_collections':
            return 'Matching spreadsheet destinations to the shop\'s synced Shopify collections.';
        case 'creating_vehicles':
            return 'Creating any canonical vehicle records that do not exist yet.';
        case 'reading_configurator_data':
            return 'Reading variant identifiers and the selected configurator’s dynamic attribute columns.';
        case 'writing_configurator_data':
            return 'Writing variant-level metafields to Shopify in safe batches.';
        case 'validating_configurator_products':
            return 'Checking required attributes, fitments, and unique variant resolution before assignment.';
        case 'assigning_configurator_products':
            return 'Applying the selected configurator to products that passed validation.';
        case 'tagging':
            return 'Refreshing product tags for changed fitments.';
        default:
            return null;
    }
};

type Props = {
    items: FitmentImportHistoryItem[];
    archivingIds: Record<number, boolean>;
    onArchive: (queueId: number) => void;
    onDownload: (item: FitmentImportHistoryItem) => void;
    onDownloadReview?: (item: FitmentImportHistoryItem) => void;
    downloadingIds: Record<number, boolean>;
    downloadingReviewIds?: Record<number, boolean>;
    reviewingProductTagIds?: Record<number, boolean>;
    undoingProductTagIds?: Record<number, boolean>;
    onReviewProductTags?: (item: FitmentImportHistoryItem) => void;
    onUndoProductTags?: (item: FitmentImportHistoryItem) => void;
};

type GroupedHistoryItems = ImportDateGroup & {
    items: FitmentImportHistoryItem[];
};

const groupHistoryItems = (items: FitmentImportHistoryItem[]) => (
    items.reduce<GroupedHistoryItems[]>((groups, item) => {
        const dateGroup = getImportDateGroup(item.addedOn);
        const currentGroup = groups[groups.length - 1];

        if (currentGroup?.key === dateGroup.key) {
            currentGroup.items.push(item);
            return groups;
        }

        groups.push({
            ...dateGroup,
            items: [item],
        });
        return groups;
    }, [])
);

const getHistoryTimingLabel = (item: FitmentImportHistoryItem, stageLabel: string | null) => {
    const normalizedState = item.state.trim().toLowerCase();
    const isTerminalState = normalizedState.includes('complete')
        || normalizedState.includes('failed')
        || normalizedState.includes('error')
        || normalizedState === 'applied'
        || normalizedState === 'undone'
        || normalizedState === 'cancelled'
        || normalizedState === 'canceled';

    if (item.completedAt) {
        return `Finished ${formatImportDate(item.completedAt)}`;
    }

    if (item.startedAt) {
        return stageLabel && !isTerminalState
            ? `${stageLabel} · Started ${formatImportDate(item.startedAt)}`
            : `Started ${formatImportDate(item.startedAt)}`;
    }

    return isTerminalState ? null : stageLabel;
};

const getResultSummary = (item: FitmentImportHistoryItem) => {
    const countLabel = (count: number | undefined, singular: string, plural: string) =>
        typeof count === 'number' ? count.toLocaleString() + ' ' + (count === 1 ? singular : plural) : null;
    if (item.importFormat === 'product_tags' && item.awaitingProductTagApproval) {
        return countLabel(item.productTagWillApplyCount, 'row ready to apply', 'rows ready to apply');
    }
    if (getHistoryItemTone(item.state) === 'info' || getHistoryItemTone(item.state) === 'critical'
        || ['undone', 'cancelled', 'canceled', 'pending', 'in_progress', 'in progress'].includes(item.state.trim().toLowerCase())) return null;
    switch (item.importFormat) {
        case 'csv_xlsx':
            return countLabel(item.csvXlsxAppliedAssignmentCount ?? item.appliedRecordCount, 'product assignment', 'product assignments');
        case 'fitment_export':
            return countLabel(item.appliedRecordCount ?? item.parsedRecordCount, 'assignment exported', 'assignments exported');
        case 'collection_mappings_csv_xlsx':
            return countLabel(item.collectionMappingAddedCount, 'mapping added', 'mappings added');
        case 'configurator_data_csv_xlsx':
            return countLabel(item.configuratorUpdatedVariantCount, 'variant updated', 'variants updated');
        default:
            return countLabel(item.appliedRecordCount, 'row applied', 'rows applied');
    }
};

const ImportHistoryRow = ({ item, archivingIds, onArchive, onDownload, onDownloadReview, downloadingIds,
    downloadingReviewIds = {}, reviewingProductTagIds = {}, undoingProductTagIds = {},
    onReviewProductTags, onUndoProductTags,
}: Omit<Props, 'items'> & { item: FitmentImportHistoryItem }) => {
    const [expanded, setExpanded] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const detailsId = useId();
    const titleId = useId();
    const progressValue = typeof item.progressPercent === 'number' ? Math.max(0, Math.min(100, item.progressPercent)) : undefined;
    const stageLabel = formatStageLabel(item.processingStage);
    const timingLabel = getHistoryTimingLabel(item, stageLabel);
    const tone = getHistoryItemTone(item.state);
    const normalizedState = item.state.trim().toLowerCase();
    const canDownloadExport = item.importFormat === 'fitment_export' && !!item.storedFileName
        && ['completed', 'complete'].includes(normalizedState);
    const isActive = tone === 'info' || ['pending', 'in_progress', 'in progress'].includes(normalizedState);
    const stageProgress = getStageProgress(item);
    const stageDiagnostics = getStageDiagnostics(item);
    const recordMetrics = getRecordMetrics(item);
    const resultSummary = getResultSummary(item);
    const canReviewFile = ['csv_xlsx', 'collection_mappings_csv_xlsx', 'configurator_data_csv_xlsx'].includes(item.importFormat)
        && !!item.storedFileName && !!onDownloadReview;
    const needsFileReview = canReviewFile && (normalizedState.includes('skipped') || normalizedState.includes('with review'));
    const hasTagReview = item.importFormat === 'product_tags' && !!onReviewProductTags;
    const addedOn = new Date(item.addedOn);
    const timeLabel = Number.isNaN(addedOn.getTime()) ? 'Date unavailable'
        : addedOn.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    const importMode = item.importFormat === 'csv_xlsx' ? item.csvXlsxImportMode === 'replace_matched_skus' ? 'Replace matched SKUs' : 'Append' : null;

    return (
        <article className="pf-import-history-item" aria-labelledby={titleId}>
            <header className="pf-import-history-item__header">
                <div className="pf-import-history-item__identity">
                    <span className="pf-import-history-item__file-icon" aria-hidden="true"><Icon source={FileIcon} tone="subdued" /></span>
                    <div className="pf-import-history-item__title">
                        <Text as="h4" variant="headingSm" id={titleId}>{item.originalFileName || 'Unnamed file'}</Text>
                        <div className="pf-import-history-item__meta">
                            <span>{getImportTypeLabel(item.importFormat)}</span>
                            {importMode && <span>{importMode}</span>}
                            <time dateTime={Number.isNaN(addedOn.getTime()) ? undefined : item.addedOn}>{timeLabel}</time>
                        </div>
                        {resultSummary && <div className="pf-import-history-item__result"><Text as="p" variant="bodySm" fontWeight="medium">{resultSummary}</Text></div>}
                        {needsFileReview && <div className="pf-import-history-item__review">
                            <Button variant="plain" onClick={() => onDownloadReview?.(item)} loading={!!downloadingReviewIds[item.queueId]}>
                                {normalizedState.includes('skipped') ? 'Review skipped SKUs' : 'Review import issues'}
                            </Button>
                        </div>}
                        {hasTagReview && item.awaitingProductTagApproval && <div className="pf-import-history-item__review">
                            <Button variant="plain" onClick={() => onReviewProductTags?.(item)} loading={!!reviewingProductTagIds[item.queueId]}>Review import</Button>
                        </div>}
                    </div>
                </div>
                <div className="pf-import-history-item__actions">
                    {getStatusBadge(item.state)}
                    {item.isArchived && <Badge>Archived</Badge>}
                    {canDownloadExport && <Button icon={ArrowDownIcon} size="slim" onClick={() => onDownload(item)}
                        loading={!!downloadingIds[item.queueId]}>Download</Button>}
                    <Button variant="plain" disclosure={expanded ? 'up' : 'down'} ariaExpanded={expanded} ariaControls={detailsId}
                        onClick={() => setExpanded(current => !current)}>{expanded ? 'Hide details' : 'Show details'}</Button>
                    {!item.isArchived && <Popover active={menuOpen} onClose={() => setMenuOpen(false)}
                        activator={<Button icon={MenuHorizontalIcon} variant="tertiary"
                            accessibilityLabel={'More actions for ' + (item.originalFileName || 'import')}
                            loading={!!archivingIds[item.queueId]} onClick={() => setMenuOpen(current => !current)} />}>
                        <ActionList items={[{ content: item.importFormat === 'fitment_export' ? 'Archive export' : 'Archive import', icon: ArchiveIcon,
                            disabled: !!archivingIds[item.queueId], onAction: () => { setMenuOpen(false); onArchive(item.queueId); } }]} />
                    </Popover>}
                </div>
            </header>

            {isActive && <div className="pf-import-history-item__progress" role="status">
                <BlockStack gap="200">
                    <BlockStack gap="100">
                        <InlineStack align="space-between" gap="200">
                            <Text as="p" variant="bodySm">{item.processingMessage || stageLabel || 'Waiting to start'}</Text>
                            {progressValue !== undefined && <Text as="p" variant="bodySm" tone="subdued">{progressValue}%</Text>}
                        </InlineStack>
                        {progressValue !== undefined && <ProgressBar progress={progressValue} size="small" />}
                    </BlockStack>
                    {stageProgress && <BlockStack gap="100">
                        <Text as="p" variant="bodySm" tone="subdued">{stageProgress.label}</Text>
                        <ProgressBar progress={stageProgress.value} size="small" />
                    </BlockStack>}
                </BlockStack>
            </div>}
            {(item.error || tone === 'critical') && <div className="pf-import-history-item__error">
                <Text as="p" variant="bodySm" tone="critical">{item.error || 'This run failed. Show details for more information.'}</Text>
            </div>}

            {expanded && <div id={detailsId} className="pf-import-history-item__details">
                <BlockStack gap="300">
                    <Text as="p" variant="bodySm">{getMappingRulesSummary(item)}</Text>
                    {hasRecordMetrics(item) && <dl className="pf-import-history-item__metrics">
                        {recordMetrics.map(metric => <div className="pf-import-history-item__metric" key={metric.label}>
                            <dt><Text as="span" variant="bodySm" tone="subdued">{metric.label}</Text></dt>
                            <dd><Text as="span" variant="headingSm">{metric.value}</Text></dd>
                        </div>)}
                    </dl>}
                    {isActive && stageDiagnostics && <Text as="p" variant="bodySm" tone="subdued">{stageDiagnostics}</Text>}
                    {typeof item.taggingTotalCount === 'number' && item.taggingTotalCount > 0 && <Text as="p" variant="bodySm" tone="subdued">
                        Tagging: Processed {formatCount(item.taggingProcessedCount)} / {formatCount(item.taggingTotalCount)}, Updated {formatCount(item.taggingUpdatedCount)}
                    </Text>}
                    {item.importFormat === 'product_tags' && typeof item.productTagPreviewTotalCount === 'number' && <Text as="p" variant="bodySm" tone="subdued">
                        Preview: {formatCount(item.productTagWillApplyCount)} row(s) ready, {formatCount(item.productTagWillCreateCount)} new vehicle value(s), {formatCount(item.productTagSkippedCount)} skipped across {formatCount(item.productTagPreviewProductCount)} product(s).
                    </Text>}
                    <footer className="pf-import-history-item__footer">
                        <BlockStack gap="100">
                            <Text as="p" variant="bodySm" tone="subdued">Queued {formatImportDate(item.addedOn)}</Text>
                            {timingLabel && <Text as="p" variant="bodySm" tone="subdued">{timingLabel}</Text>}
                            {item.isArchived && item.archivedAt && <Text as="p" variant="bodySm" tone="subdued">Archived {formatImportDate(item.archivedAt)}</Text>}
                        </BlockStack>
                        <InlineStack gap="200" blockAlign="center" wrap>
                            {hasTagReview && <Button icon={ViewIcon} size="slim" onClick={() => onReviewProductTags?.(item)}
                                loading={!!reviewingProductTagIds[item.queueId]}>{item.awaitingProductTagApproval ? 'Review' : 'Preview'}</Button>}
                            {item.importFormat === 'product_tags' && item.productTagReviewStatus?.toLowerCase() === 'applied' && onUndoProductTags && <Button
                                icon={UndoIcon} size="slim" tone="critical" variant="tertiary" onClick={() => onUndoProductTags(item)}
                                loading={!!undoingProductTagIds[item.queueId]}>Undo</Button>}
                            {canReviewFile && <Button icon={ViewIcon} size="slim" onClick={() => onDownloadReview?.(item)}
                                loading={!!downloadingReviewIds[item.queueId]}>Review file</Button>}
                            {item.storedFileName && item.importFormat !== 'fitment_export' && <Button icon={ArrowDownIcon} size="slim" variant="plain" onClick={() => onDownload(item)}
                                loading={!!downloadingIds[item.queueId]}>Download</Button>}
                        </InlineStack>
                    </footer>
                </BlockStack>
            </div>}
        </article>
    );
};

const ImportHistoryCards = ({ items, ...props }: Props) => {
    const headingPrefix = useId();
    return <div className="pf-import-history-list">
        {groupHistoryItems(items).map((group, groupIndex) => {
            const headingId = headingPrefix + '-' + groupIndex;
            return <section aria-labelledby={headingId} className="pf-import-history-group" key={group.key + '-' + groupIndex}>
                <div className="pf-import-history-group__header">
                    <Text as="h3" variant="bodySm" tone="subdued" id={headingId}>{group.label}</Text>
                    <Text as="span" variant="bodySm" tone="subdued">{group.items.length} {group.items.length === 1 ? 'run' : 'runs'}</Text>
                </div>
                <div className="pf-import-history-group__items">
                    {group.items.map(item => <ImportHistoryRow key={item.queueId} item={item} {...props} />)}
                </div>
            </section>;
        })}
    </div>;
};

export default ImportHistoryCards;
